import React, { useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../contexts/AuthContext';
import { fetchRowerGuardianshipStatus } from '../../services/rower/rowerGuardianshipService';
import RowerConsentLockdown, {
  REQUEST_PARENT_CONSENT_PATH,
  ROWER_CONSENT_NOTIFICATIONS_PATH,
} from '../rower/RowerConsentLockdown';

export { REQUEST_PARENT_CONSENT_PATH, ROWER_CONSENT_NOTIFICATIONS_PATH };

const LOCKDOWN_ALLOWED_PATHS = new Set([
  REQUEST_PARENT_CONSENT_PATH,
  ROWER_CONSENT_NOTIFICATIONS_PATH,
]);

function consentLockdownCacheKey(userId: string) {
  return `rower-needs-parental-consent:${userId}`;
}

function readCachedNeedsConsent(userId: string | undefined): boolean {
  if (!userId || typeof sessionStorage === 'undefined') return false;
  return sessionStorage.getItem(consentLockdownCacheKey(userId)) === 'true';
}

function writeCachedNeedsConsent(userId: string | undefined, needsConsent: boolean) {
  if (!userId || typeof sessionStorage === 'undefined') return;
  sessionStorage.setItem(consentLockdownCacheKey(userId), needsConsent ? 'true' : 'false');
}

interface Props {
  children: React.ReactNode;
}

/**
 * GLY-363: locks down the rower app when a minor needs parental (re-)consent,
 * either because it was never given or because a parent/guardian withdrew it.
 * Shows a full-screen blurred modal over the app chrome; only the request-consent
 * page and consent inbox remain reachable. Composed inside RowerRoute so every
 * existing rower route gets this for free.
 *
 * While status is loading, adults see the normal app (no "checking access" overlay).
 * Athletes who were previously locked down stay covered via a session cache so the
 * dashboard does not flash through on their next sign-in.
 */
export const RowerConsentGate: React.FC<Props> = ({ children }) => {
  const { user, session } = useAuth();
  const location = useLocation();

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['rower-guardianship-status', user?.id],
    queryFn: () => fetchRowerGuardianshipStatus(session!.access_token),
    enabled: !!user?.id && !!session?.access_token,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (typeof data?.needsParentalConsent === 'boolean') {
      writeCachedNeedsConsent(user?.id, data.needsParentalConsent);
    }
  }, [data?.needsParentalConsent, user?.id]);

  const isAllowedDuringLockdown = LOCKDOWN_ALLOWED_PATHS.has(location.pathname);
  const awaitingStatus = isLoading || (isFetching && data === undefined);
  const cachedNeedsConsent = readCachedNeedsConsent(user?.id);

  // Only cover the shell while loading if this athlete was already known to need lockdown.
  if (awaitingStatus) {
    return cachedNeedsConsent ? <RowerConsentLockdown loading /> : <>{children}</>;
  }

  if (data?.needsParentalConsent) {
    if (!isAllowedDuringLockdown) {
      return (
        <>
          <RowerConsentLockdown loading />
          <Navigate to={REQUEST_PARENT_CONSENT_PATH} replace state={{ from: location }} />
        </>
      );
    }
    return <RowerConsentLockdown>{children}</RowerConsentLockdown>;
  }

  return <>{children}</>;
};

export default RowerConsentGate;
